import type {
	API_CreateMedicalRecordRequest,
	API_GenerateMedicalRecordSummaryRequest,
	API_GenerateMedicalRecordSummaryResponse,
	API_GetMedicalRecordSummaryStatusResponse,
	API_ListCustomerMedicalRecordsPaginatedResponse,
	API_ListCustomerMedicalRecordsResponse,
	API_UpdateMedicalRecordRequest,
	MedicalRecord,
	PaginationRequest,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchCustomerMedicalRecords = (
	token: string,
	organizationId: string,
	customerId: string,
): Promise<API_ListCustomerMedicalRecordsResponse> => {
	return api
		.get(
			`/organizations/${organizationId}/customers/${customerId}/medical-records`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);
};

export const fetchCustomerMedicalRecordsPaginated = (
	token: string,
	organizationId: string,
	customerId: string,
	paginationParams: PaginationRequest,
): Promise<API_ListCustomerMedicalRecordsPaginatedResponse> => {
	const params = {
		...paginationParams,
	};
	return api
		.get(
			`/organizations/${organizationId}/customers/${customerId}/medical-records`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
				params,
			},
		)
		.then((res) => res.data);
};

export const fetchMedicalRecordById = (
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
): Promise<MedicalRecord> =>
	api
		.get(
			`/organizations/${organizationId}/customers/${customerId}/medical-records/${recordId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const createMedicalRecord = (
	token: string,
	organizationId: string,
	customerId: string,
	data: API_CreateMedicalRecordRequest,
): Promise<MedicalRecord> =>
	api
		.post(
			`/organizations/${organizationId}/customers/${customerId}/medical-records`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const updateMedicalRecord = (
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
	data: API_UpdateMedicalRecordRequest,
): Promise<MedicalRecord> =>
	api
		.put(
			`/organizations/${organizationId}/customers/${customerId}/medical-records/${recordId}`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const deleteMedicalRecord = (
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
): Promise<void> =>
	api
		.delete(
			`/organizations/${organizationId}/customers/${customerId}/medical-records/${recordId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Query options
export const fetchCustomerMedicalRecordsQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
) =>
	queryOptions({
		queryKey: ["customer_medical_records", organizationId, customerId, userId],
		queryFn: () =>
			fetchCustomerMedicalRecords(token, organizationId, customerId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchMedicalRecordByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
) =>
	queryOptions({
		queryKey: ["medical_record", organizationId, customerId, recordId, userId],
		queryFn: () =>
			fetchMedicalRecordById(token, organizationId, customerId, recordId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerMedicalRecordsPaginatedQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	paginationParams: PaginationRequest,
) =>
	queryOptions({
		queryKey: [
			"customer_medical_records_paginated",
			organizationId,
			customerId,
			userId,
			paginationParams,
		],
		queryFn: () =>
			fetchCustomerMedicalRecordsPaginated(
				token,
				organizationId,
				customerId,
				paginationParams,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateMedicalRecord = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			data: API_CreateMedicalRecordRequest;
		}) => createMedicalRecord(token, organizationId, customerId, data),
		toastMessages: {
			loading: t("medicalRecords.creating"),
			success: t("medicalRecords.createSuccess"),
			error: t("medicalRecords.createError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch medical records
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records_paginated",
					variables.organizationId,
					variables.customerId,
				],
			});
		},
	});
};

export const useUpdateMedicalRecord = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			recordId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			recordId: string;
			data: API_UpdateMedicalRecordRequest;
		}) =>
			updateMedicalRecord(token, organizationId, customerId, recordId, data),
		toastMessages: {
			loading: t("medicalRecords.updating"),
			success: t("medicalRecords.updateSuccess"),
			error: t("medicalRecords.updateError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch medical records
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records_paginated",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"medical_record",
					variables.organizationId,
					variables.customerId,
					variables.recordId,
				],
			});
		},
	});
};

export const useDeleteMedicalRecord = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			recordId,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			recordId: string;
		}) => deleteMedicalRecord(token, organizationId, customerId, recordId),
		toastMessages: {
			loading: t("medicalRecords.deleting"),
			success: t("medicalRecords.deleteSuccess"),
			error: t("medicalRecords.deleteError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch medical records
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records_paginated",
					variables.organizationId,
					variables.customerId,
				],
			});
			// Remove the specific record from cache
			queryClient.removeQueries({
				queryKey: [
					"medical_record",
					variables.organizationId,
					variables.customerId,
					variables.recordId,
				],
			});
		},
	});
};

// Generate AI summary for anamnesis records
export const generateMedicalRecordSummary = (
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
	data?: API_GenerateMedicalRecordSummaryRequest,
): Promise<API_GenerateMedicalRecordSummaryResponse> =>
	api
		.post(
			`/organizations/${organizationId}/customers/${customerId}/medical-records/${recordId}/generate-summary`,
			data || {},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const useGenerateMedicalRecordSummary = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			recordId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			recordId: string;
			data?: API_GenerateMedicalRecordSummaryRequest;
		}) =>
			generateMedicalRecordSummary(
				token,
				organizationId,
				customerId,
				recordId,
				data,
			),
		toastMessages: {
			loading: t("medicalRecords.generatingSummary"),
			success: t("medicalRecords.generateSummarySuccess"),
			error: t("medicalRecords.generateSummaryError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch medical records
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records_paginated",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"medical_record",
					variables.organizationId,
					variables.customerId,
					variables.recordId,
				],
			});
		},
	});
};

// Get summary status for a medical record
export const fetchMedicalRecordSummaryStatus = (
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
): Promise<API_GetMedicalRecordSummaryStatusResponse> =>
	api
		.get(
			`/organizations/${organizationId}/customers/${customerId}/medical-records/${recordId}/summary-status`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const fetchMedicalRecordSummaryStatusQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
	recordId: string,
) =>
	queryOptions({
		queryKey: [
			"medical_record_summary_status",
			organizationId,
			customerId,
			recordId,
			userId,
		],
		queryFn: () =>
			fetchMedicalRecordSummaryStatus(
				token,
				organizationId,
				customerId,
				recordId,
			),
		staleTime: 30 * 1000, // 30 seconds - shorter for status checks
	});
